/**
 * Loan Applicant Dashboard — Borrower Home page (Screen 1).
 *
 * Per the Fiops Borrower Portal v3 design pass
 * (`/tmp/design-pass/borrower/specs/home-and-astor.md` — Screen 1), the Home
 * page is now a concierge-style hybrid layout composed of:
 *
 *   1. Greeting header — `GreetingHeader` (date eyebrow + time-of-day
 *      hero greeting + inline stats + secondary/primary CTAs)
 *   2. Astor briefing hero — `AstorBriefing`
 *   3. KPI strip (4 tiles) using `KpiTile` from the design system
 *   4. Active loans panel — `ActiveLoansPanel`
 *   5. Portfolio insights strip (3 cards) — `PortfolioInsightsStrip`
 *
 * The legacy "Application Progress + Document Stats + E-Signatures" surface
 * has been replaced. The signing flow now lives at `/documents-to-sign`
 * (already a nav item driven by `DocumentsToSignWidget`).
 */

import AppsIcon from '@mui/icons-material/Apps';
import DollarIcon from '@mui/icons-material/AttachMoney';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HomeIcon from '@mui/icons-material/Home';
import { GlassAlert, Grid, iconSizes, KpiTile, Stack, Typography, useI18n } from '@ops-matrix/design-system-ui';

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ActiveLoansPanel } from '../../components/dashboard/ActiveLoansPanel';
import { AstorBriefing } from '../../components/dashboard/AstorBriefing';
import { GreetingHeader, type GreetingHeaderStat } from '../../components/dashboard/GreetingHeader';
import { PortfolioInsightsStrip } from '../../components/dashboard/PortfolioInsightsStrip';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useConsumerAuth } from '../../contexts/ConsumerAuthContext';
import { useConsumerCases, useConsumerProposals } from '../../lib/trpc/hooks';

/** Compact USD formatter — "$2.4M" / "$420K" / "$0". */
function formatCompactUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1,
  }).format(value);
}

interface CaseSummary {
  id: string;
  loanAmount?: number | null;
  closeDate?: string | null;
  status?: string | null;
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
}

export default function LoanApplicantDashboard(): React.ReactElement {
  const { t } = useI18n();
  const location = useLocation();
  const { consumer } = useConsumerAuth();

  // Issue #387 — submission success banner (preserved from prior dashboard).
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
  useEffect(() => {
    const state = location.state as { applicationSubmitted?: boolean } | null;
    if (state?.applicationSubmitted) {
      setShowSubmitSuccess(true);
      window.history.replaceState({}, document.title);
      const timer = setTimeout(() => setShowSubmitSuccess(false), 10000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.state]);

  // Live data for KPI strip. The backend doesn't yet emit an Astor-ranked
  // "needs action" count — we approximate from the same `consumerCases.list`
  // response so the tile reads non-trivially today.
  // TODO(#followup): replace the approximation with a server-side
  // `consumerCases.summary` endpoint that returns the four KPI scalars in one
  // round-trip plus the Astor-ranked needsAction count.
  const {
    data: casesData,
    isLoading: casesLoading,
    error: casesError,
  } = useConsumerCases({
    page: 1,
    limit: 50,
  });
  const { data: proposalsData, isLoading: proposalsLoading } = useConsumerProposals({
    page: 1,
    limit: 50,
  });

  const firstName = useMemo(() => {
    const name = consumer?.name?.trim();
    if (!name) return t('consumer.home.greeting.defaultFirstName');
    return name.split(/\s+/)[0] ?? name;
  }, [consumer?.name, t]);

  const kpis = useMemo(() => {
    const cases = (casesData?.data ?? []) as CaseSummary[];
    const activeCases = cases.filter(
      (c) => !['closed', 'rejected', 'withdrawn'].includes((c.status ?? '').toLowerCase()),
    );
    const totalExposure = activeCases.reduce((sum, c) => sum + (c.loanAmount ?? 0), 0);
    const closeDays = activeCases.map((c) => daysUntil(c.closeDate)).filter((d): d is number => d !== null);
    const nextCloseDays = closeDays.length > 0 ? Math.min(...closeDays) : null;
    // TODO(#followup): no Astor priority bit yet — use submissions-in-document-collection as a proxy.
    const needsAction = activeCases.filter((c) => (c.status ?? '').toLowerCase() === 'pending').length;
    // `consumerProposals.list` returns `{ data, total, page, limit, totalPages }`.
    const offersAwaiting = ((proposalsData as { data?: { status?: string }[] } | undefined)?.data ?? []).filter((p) =>
      ['sent', 'pending', 'submitted'].includes((p.status ?? '').toLowerCase()),
    ).length;
    return {
      activeCount: activeCases.length,
      needsActionCount: needsAction,
      totalExposure,
      entityCount: activeCases.length, // proxy until entity rollup endpoint ships
      nextCloseDays,
      offersAwaiting,
    };
  }, [casesData, proposalsData]);

  const isLoading = casesLoading || proposalsLoading;

  // Stats projected into the greeting strip — reuses the same `kpis` object
  // computed above so we don't recompute. Hide stats while loading.
  const greetingStats = useMemo<GreetingHeaderStat[]>(() => {
    if (isLoading) return [];
    const items: GreetingHeaderStat[] = [
      {
        id: 'active',
        tone: 'primary',
        label: t('consumer.home.greeting.statActiveLoans', { count: kpis.activeCount }),
      },
      {
        id: 'exposure',
        tone: 'secondary',
        label: t('consumer.home.greeting.statTotalExposure', { amount: formatCompactUsd(kpis.totalExposure) }),
      },
    ];
    if (kpis.nextCloseDays !== null) {
      items.push({
        id: 'next-close',
        tone: 'warning',
        showClockIcon: true,
        label: t('consumer.home.greeting.statNextClose', { days: kpis.nextCloseDays }),
      });
    }
    return items;
  }, [isLoading, kpis, t]);

  return (
    <PageTemplate variant="dashboard" titleKey="consumer.home.greeting.title" icon={HomeIcon}>
      {showSubmitSuccess && (
        <GlassAlert severity="success" visible={true} onClose={() => setShowSubmitSuccess(false)} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('dashboard.applicationSubmittedSuccess')}
          </Typography>
          <Typography variant="body2">{t('dashboard.applicationSubmittedDesc')}</Typography>
        </GlassAlert>
      )}

      {casesError && (
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
          {t('dashboard.failedToLoadApplication')}
        </GlassAlert>
      )}

      <Stack spacing={4}>
        {/* 1. Greeting header — date eyebrow + hero greeting + inline stats + actions */}
        <GreetingHeader firstName={firstName} stats={greetingStats} />

        {/* 2. Astor briefing hero */}
        <AstorBriefing firstName={firstName} />

        {/* 3. KPI strip */}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiTile
              tone="mint"
              icon={<AppsIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.activeLoans')}
              value={isLoading ? '—' : String(kpis.activeCount)}
              sub={t('consumer.home.kpi.activeLoansSubtitle', { count: kpis.needsActionCount })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiTile
              tone="id"
              icon={<DollarIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.totalExposure')}
              value={isLoading ? '—' : formatCompactUsd(kpis.totalExposure)}
              sub={t('consumer.home.kpi.totalExposureSub', { count: kpis.entityCount })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiTile
              tone="amber"
              icon={<CalendarIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.nextClose')}
              value={
                isLoading
                  ? '—'
                  : kpis.nextCloseDays !== null
                    ? t('consumer.home.kpi.nextCloseDays', { days: kpis.nextCloseDays })
                    : t('consumer.home.kpi.nextCloseNone')
              }
              sub={t('consumer.home.kpi.nextCloseSub')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <KpiTile
              tone="orange"
              icon={<HandshakeIcon sx={{ fontSize: iconSizes.base }} />}
              label={t('consumer.home.kpi.offersAwaiting')}
              value={isLoading ? '—' : String(kpis.offersAwaiting)}
              sub={t('consumer.home.kpi.offersAwaitingSub')}
            />
          </Grid>
        </Grid>

        {/* 4. Active loans panel */}
        <ActiveLoansPanel />

        {/* 5. Portfolio insights strip */}
        <PortfolioInsightsStrip />
      </Stack>
    </PageTemplate>
  );
}
